import { ApisauceInstance } from 'apisauce'
import { PaginationResponse } from '@codeleap/query'
import { Message } from 'stream-chat'
import {getCustomMessageData, GetCustomMessageDataParams} from './utils'
import { ChatListUsersFilters, ChatChannel, ChatUser, ChatMember, ChatGroupBody, InsertChatUser, ChatPhoto, ListChatFilter, SendSystemMessageProps, ChatChannelInstance, UpdatePushPreferencesBody, ChatPackageConstructorOptions } from './types'
import { getUser } from './stores'

const LOCAL = false // Set to true for local development, false for production
const LOCAL_URL = 'http://localhost:8000/'

export class ChatAPI {
  protected LOCAL: boolean
  protected api: ApisauceInstance
  protected getChatClient: ChatPackageConstructorOptions['getChatClient']

  constructor(props: ChatPackageConstructorOptions) {
    this.api = props.api
    this.getChatClient = props.getChatClient
  }

  private getOptions(options?: object) {
    if (LOCAL) {
      return { ...(options || {}), baseURL: LOCAL_URL }
    }
    return options || {}
  }

  async createDM(user: InsertChatUser) {
    const response = await this.api.post<ChatChannel>(
      'chat/',
      { member: user?.id, name: user?.name },
      this.getOptions()
    )
    return response
  }

  async createPhoto(file: File) {
    const response = await this.api.post<ChatPhoto>(
      'chat/photos/',
      { file },
      {
        ...this.getOptions(),
        // @ts-ignore
        multipart: true
      }
    )
    return response
  }

  async createGroup(body: ChatGroupBody) {
    const response = await this.api.post<ChatChannel>(
      'chat/groups/',
      body,
      this.getOptions()
    )
    return response
  }

  async updateGroup(id: ChatChannel['id'], body: Pick<ChatGroupBody, 'name' | 'image'>) {
    const response = await this.api.patch<ChatChannel>(
      `chat/groups/${id}/`,
      body,
      this.getOptions()
    )
    return response
  }

  async deleteGroup(id: ChatChannel['id']) {
    const response = await this.api.delete(
      `chat/groups/${id}/`,
      undefined,
      this.getOptions()
    )

    return response
  }

  async retrieveGroup(id: ChatChannel['id']) {
    const response = await this.api.get<ChatChannel>(
      `chat/groups/${id}/`,
      undefined,
      this.getOptions()
    )
    return response
  }

  async listMembers(id: ChatChannel['id'], filters?: ChatListUsersFilters) {
    // Backend returns `ChannelMemberResponse`-shaped rows (each carries a
    // `user_id` that the query layer mirrors into the react-query `id` field
    // in `queries/members.ts`); the previous `PaginationResponse<ChatUser>`
    // annotation was wrong — ChatUser does not expose `user_id`.
    const response = await this.api.get<PaginationResponse<ChatMember>>(
      `chat/${id}/members/`,
      { include_me: true, ...filters },
      this.getOptions()
    )
    return response
  }

  async addMembers(id: ChatChannel['id'], members: InsertChatUser[], send_system_message: boolean = true) {
    const response = await this.api.post<ChatChannel>(
      `chat/${id}/members/`,
      { members, send_system_message },
      this.getOptions()
    )
    return response
  }

  async removeMember(id: ChatChannel['id'], memberId: string, send_system_message: boolean = true) {
    const response = await this.api.delete(
      `chat/${id}/members/${memberId}/`,
      { send_system_message },
      this.getOptions()
    )
    return response
  }

  async listUsers(filters?: ChatListUsersFilters) {
    const response = await this.api.get<PaginationResponse<ChatUser>>(
      'chat/users/',
      filters,
      this.getOptions()
    )
    return response
  }


  async retrieveUser(id: ChatUser['id']) {
    const response = await this.api.get<ChatUser>(
      `chat/users/${id}/`,
      undefined,
      this.getOptions()
    )
    return response
  }

  async updateUser(id: string, user: Partial<ChatUser>) {
    const response = await this.api.patch<ChatUser>(
      `chat/users/${id}/`,
      user,
      this.getOptions()
    )
    return response
  }

  async createUser() {
    const response = await this.api.post<ChatUser>(
      'chat/users/',
      {},
      this.getOptions()
    )
    return response
  }

  async addModerators(
    id: ChatChannel['id'],
    members: Array<string | { id: string, name?: string }>,
    send_system_message: boolean = true
  ) {
    const response = await this.api.post<ChatChannel>(
      `chat/${id}/moderators/`,
      { members, send_system_message },
      this.getOptions()
    )
    return response
  }

  async removeModerator(id: ChatChannel['id'], memberId: string, send_system_message: boolean = true) {
    const response = await this.api.delete(
      `chat/${id}/moderators/${memberId}/`,
      { send_system_message },
      this.getOptions()
    )
    return response
  }

  async listChats(props: ListChatFilter & { limit?: number; offset?: number }) {
    const client = await this.getChatClient()

    const { limit = 20, offset = 0, filters, sort, options } = props

    const results = await client.queryChannels(
      filters,
      sort,
      {
        ...options,
        limit,
        offset,
      },
    )

    return {
      results,
      count: results.length,
      hasMore: results.length === limit,
    }
  }

  async sendMessageSystem({ channel, text, target_id, target_name, targets, action, count }: SendSystemMessageProps) {
    const chatUser = getUser()
    const chatClient = await this.getChatClient()
    const channelInstance = chatClient.channel(channel.type, channel.id)
    await channelInstance.watch()
    return channelInstance.sendMessage({ 
      text, 
      type: 'system', 
      user: chatUser, 
      user_id: chatUser?.id, 
      silent: true,
      // @ts-ignore
      custom: {
        target_id, 
        target_name,
        targets,
        action,
        count,
      },
    }, 
    {
      skip_push: true
    })
  }

  // This function must be called after a poll is created and pollComposer has the poll data
  async sendPoll(
    channel: ChatChannelInstance,
    pollData: { name: string, id: string },
    opts?: { quotedMessageId?: string },
  ) {
    return this.sendMessage(channel, {
      poll_id: pollData?.id,
      pollName: pollData?.name,
      quoted_message_id: opts?.quotedMessageId,
    })
  }

  async sendMessage(channel: ChatChannelInstance, messageData: Message & GetCustomMessageDataParams) {
    const customData = getCustomMessageData(messageData)

    return channel.sendMessage({
      user: getUser(),
      ...messageData,
      ...customData
    })
  }

  async fetchExcludedWords() {
    return this.api.get<{ results: { word: string }[] }>('chat/excluded-words/', undefined, this.getOptions())
  }

  async updatePushPreferences(body: UpdatePushPreferencesBody) {
    const response = await this.api.post(
      `chat/push_preferences/`,
      {
        chat_id: body?.chatId,
        chat_level: body?.chatLevel,
        disabled_until: body?.disabledUntil,
      },
      this.getOptions()
    )

    return response
  }
}
